import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /grades/:gradeId/tracks/:trackId/lessons/:lessonId — the lesson HUB. Every
 * lesson-scoped concern in one place:
 *
 *   - Details header (title + flags + recommended-path readout).
 *   - Clips card (drag-to-reorder, create/edit modals).
 *   - Passing-assessment status: chip if attached (click → /assessments/:id
 *     with back-link), else "הוסף בוחן מעבר" chooser (create-new opens the
 *     create drawer with lesson_id prefilled; on save navigates to
 *     /assessments/:id).
 *   - Prereqs section (target='lesson', targetId=this lesson).
 *
 * The old cross-cutting /prereqs screen is gone; each entity owns its
 * prereqs on its own page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Alert, Button, Card, Modal, Popconfirm, Space, Spin, Switch, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleFilled, CheckSquareOutlined, DeleteOutlined, EditOutlined, FileTextOutlined, PlayCircleOutlined, PlusOutlined, VideoCameraOutlined, } from '@ant-design/icons';
import { deleteClip, listClips, listGrades, listLessons, listTracks, reorderClips, updateLesson, } from '../../api';
import { PageHeader } from '../../components/PageHeader';
import { notifySuccess } from '../../components/errors';
import { useLoader } from '../../components/useLoader';
import { SortableTable } from '../../components/SortableTable';
import { LessonModal } from '../../components/content/LessonModal';
import { ClipModal } from '../../components/content/ClipModal';
import { PrereqsSection } from '../../components/content/PrereqsSection';
import { PassingAssessmentChooser } from '../../components/content/PassingAssessmentChooser';
import { AssessmentEditorDrawer } from '../../components/assessments/AssessmentEditorDrawer';
export default function LessonPageScreen() {
    const nav = useNavigate();
    const { gradeId: gradeIdParam, trackId: trackIdParam, lessonId: lessonIdParam } = useParams();
    const gradeId = Number(gradeIdParam);
    const trackId = Number(trackIdParam);
    const lessonId = Number(lessonIdParam);
    const [grade, setGrade] = useState(undefined);
    const [track, setTrack] = useState(undefined);
    const [lesson, setLesson] = useState(undefined);
    const [reloadKey, setReloadKey] = useState(0);
    const bump = () => setReloadKey((k) => k + 1);
    const [editingLesson, setEditingLesson] = useState(false);
    const [quizChooserOpen, setQuizChooserOpen] = useState(false);
    const [createQuizDrawer, setCreateQuizDrawer] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const grades = await listGrades();
                const g = grades.find((x) => x.id === gradeId) ?? null;
                setGrade(g);
                if (!g) {
                    setTrack(null);
                    setLesson(null);
                    return;
                }
                const tracks = await listTracks(gradeId);
                const t = tracks.find((x) => x.id === trackId) ?? null;
                setTrack(t);
                if (!t) {
                    setLesson(null);
                    return;
                }
                const lessons = await listLessons(trackId);
                setLesson(lessons.find((x) => x.id === lessonId) ?? null);
            }
            catch {
                setGrade(null);
                setTrack(null);
                setLesson(null); /* client auto-surfaces */
            }
        })();
    }, [gradeId, trackId, lessonId, reloadKey]);
    if (grade === undefined || track === undefined || lesson === undefined)
        return _jsx(Spin, {});
    if (!grade || !track || !lesson) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", breadcrumb: [{ label: 'ניהול' }, { label: 'כיתות', to: '/grades' }, 'לא נמצא'] }), _jsx(Alert, { type: "warning", message: "\u05D4\u05E9\u05D9\u05E2\u05D5\u05E8 \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", description: _jsx(_Fragment, { children: _jsx(Link, { to: track ? `/grades/${gradeId}/tracks/${track.id}` : (grade ? `/grades/${gradeId}` : '/grades'), children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05DE\u05D4 \u05E7\u05D5\u05D3\u05DE\u05EA" }) }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: lesson.title, breadcrumb: [
                    { label: 'ניהול' },
                    { label: 'כיתות', to: '/grades' },
                    { label: grade.name, to: `/grades/${grade.id}` },
                    { label: track.name, to: `/grades/${grade.id}/tracks/${track.id}` },
                    lesson.title,
                ], actions: _jsx(Button, { icon: _jsx(EditOutlined, {}), onClick: () => setEditingLesson(true), children: "\u05E2\u05E8\u05D5\u05DA \u05E9\u05D9\u05E2\u05D5\u05E8" }) }), _jsxs(Space, { direction: "vertical", size: 16, style: { width: '100%' }, children: [_jsx(LessonDetailsCard, { lesson: lesson, gradeId: grade.id, onChanged: bump }), _jsx(PassingAssessmentCard, { lesson: lesson, onOpenAssessment: (id) => nav(`/assessments/${id}`), onOpenChooser: () => setQuizChooserOpen(true) }), _jsx(ClipsCard, { lesson: lesson }), _jsx(PrereqsSection, { target: "lesson", targetId: lesson.id, title: "\u05EA\u05E0\u05D0\u05D9-\u05E7\u05D3\u05DD \u05DC\u05E9\u05D9\u05E2\u05D5\u05E8" })] }), editingLesson && (_jsx(LessonModal, { open: true, onClose: () => setEditingLesson(false), onDone: () => { setEditingLesson(false); bump(); }, trackId: track.id, gradeId: grade.id, initial: lesson })), quizChooserOpen && (_jsx(PassingAssessmentChooser, { lesson: lesson, onClose: () => setQuizChooserOpen(false), onCreateNew: () => { setQuizChooserOpen(false); setCreateQuizDrawer(true); }, onLinkExisting: (assessmentId) => {
                    setQuizChooserOpen(false);
                    nav(`/assessments/${assessmentId}`);
                } })), createQuizDrawer && (_jsx(AssessmentEditorDrawer, { mode: "create", open: true, lockKind: "passing", prefill: { lesson_id: lesson.id, name: lesson.title }, onClose: () => setCreateQuizDrawer(false), onSaved: (id) => { setCreateQuizDrawer(false); nav(`/assessments/${id}`); } }))] }));
}
function LessonDetailsCard({ lesson, gradeId, onChanged }) {
    // Live toggle: `require_clips_watched` can be flipped straight from the
    // card without opening the full edit modal. Local state mirrors the
    // server-authoritative lesson prop; a `useEffect` resyncs whenever the
    // parent reloads (which `onChanged` triggers via `bump()`).
    const [requireClips, setRequireClips] = useState(!!lesson.require_clips_watched);
    const [savingRequireClips, setSavingRequireClips] = useState(false);
    useEffect(() => { setRequireClips(!!lesson.require_clips_watched); }, [lesson.require_clips_watched]);
    const toggleRequireClips = async (next) => {
        const prev = requireClips;
        setRequireClips(next); // optimistic flip
        setSavingRequireClips(true);
        try {
            await updateLesson(lesson.id, { require_clips_watched: next });
            notifySuccess('הגדרת השיעור עודכנה');
            onChanged?.(); // trigger parent reload so other
            // screens / modals see the new value
        }
        catch {
            setRequireClips(prev); // revert on failure
            /* client auto-surfaces the error toast via setErrorReporter */
        }
        finally {
            setSavingRequireClips(false);
        }
    };
    return (_jsx(Card, { title: _jsx(CardTitle, { icon: _jsx(FileTextOutlined, {}), text: "\u05E4\u05E8\u05D8\u05D9 \u05D4\u05E9\u05D9\u05E2\u05D5\u05E8" }), children: _jsxs("div", { style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
                gap: '12px 24px',
            }, children: [_jsx(FieldRow, { label: "\u05DB\u05D5\u05EA\u05E8\u05EA", value: lesson.title }), _jsx(FieldRow, { label: "\u05E1\u05D3\u05E8 \u05D1\u05DE\u05E1\u05DC\u05D5\u05DC", value: String(lesson.order_index) }), _jsx(FieldRow, { label: "\u05E1\u05D3\u05E8 \u05D1\u05DE\u05E1\u05DC\u05D5\u05DC \u05D4\u05DE\u05D5\u05DE\u05DC\u05E5", value: _jsxs(Space, { size: 8, children: [_jsx("span", { children: lesson.recommended_order != null ? lesson.recommended_order : 'מחוץ למסלול' }), _jsx(Link, { to: `/grades/${gradeId}?tab=recommended`, children: "\u05E0\u05D4\u05DC" })] }) }), _jsx(FieldRow, { label: "\u05E1\u05D5\u05D2 \u05D4\u05E9\u05D9\u05E2\u05D5\u05E8", value: _jsxs(Space, { size: 6, children: [lesson.is_optional && _jsx("span", { className: "chip-lavender", children: "\u05E8\u05E9\u05D5\u05EA" }), lesson.is_free && _jsxs("span", { className: "chip-mint", children: [_jsx(CheckCircleFilled, {}), " \u05D7\u05D9\u05E0\u05DD"] }), !lesson.is_optional && !lesson.is_free && _jsx("span", { style: { color: '#8c8c8c' }, children: "\u05E8\u05D2\u05D9\u05DC" })] }) }), _jsx(FieldRow, { label: "\u05D7\u05D5\u05D1\u05D4 \u05DC\u05E6\u05E4\u05D5\u05EA \u05D1\u05DB\u05DC \u05D4\u05E7\u05DC\u05D9\u05E4\u05D9\u05DD", value: _jsx(Switch, { checked: requireClips, loading: savingRequireClips, onChange: toggleRequireClips }) })] }) }));
}
function FieldRow({ label, value }) {
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 4 }, children: [_jsx("span", { style: { fontSize: 12, color: '#6D6386', fontWeight: 500 }, children: label }), _jsx("div", { style: { fontSize: 14, color: '#22174B' }, children: value })] }));
}
function CardTitle({ icon, text }) {
    return (_jsxs(Space, { size: 10, children: [_jsx("span", { style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: '#EFE9FA',
                    color: '#6B21A8',
                    fontSize: 16,
                }, children: icon }), _jsx("span", { children: text })] }));
}
function PassingAssessmentCard({ lesson, onOpenAssessment, onOpenChooser }) {
    const attached = lesson.passing_assessment_id != null;
    return (_jsx(Card, { title: _jsx(CardTitle, { icon: _jsx(CheckSquareOutlined, {}), text: "\u05D1\u05D5\u05D7\u05DF \u05DE\u05E2\u05D1\u05E8" }), children: attached ? (_jsx(ExamRow, { assessmentId: lesson.passing_assessment_id, lessonTitle: lesson.title, onOpen: onOpenAssessment })) : (_jsx(NoExamState, { onConnect: onOpenChooser })) }));
}
/**
 * List-item style row for the attached exam. Clicking anywhere on the
 * row (or the explicit "פתיחת הבוחן" link) navigates to /assessments/:id —
 * the same destination the previous "פתח בוחן" header button used.
 * We don't fetch exam metadata here (would require a second API call);
 * what we have cheaply is the lesson title + the assessment id.
 */
function ExamRow({ assessmentId, lessonTitle, onOpen }) {
    const open = () => onOpen(assessmentId);
    return (_jsxs("div", { role: "button", tabIndex: 0, onClick: open, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        } }, style: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            background: '#F8F5FF',
            border: '1px solid rgba(107, 33, 168, 0.08)',
            borderRadius: 12,
            cursor: 'pointer',
            transition: 'background 160ms ease, border-color 160ms ease',
        }, onMouseEnter: (e) => {
            e.currentTarget.style.background = '#F1EAFF';
            e.currentTarget.style.borderColor = 'rgba(107, 33, 168, 0.18)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.background = '#F8F5FF';
            e.currentTarget.style.borderColor = 'rgba(107, 33, 168, 0.08)';
        }, children: [_jsx("span", { "aria-hidden": true, style: {
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 40, height: 40, borderRadius: 10,
                    background: '#EFE9FA', color: '#6B21A8',
                    fontSize: 18, flex: 'none',
                }, children: _jsx(CheckSquareOutlined, {}) }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }, children: [_jsxs("span", { style: { fontWeight: 600, color: '#22174B' }, children: ["\u05D1\u05D5\u05D7\u05DF \u05DE\u05E2\u05D1\u05E8 \u00B7 ", lessonTitle] }), _jsxs("span", { style: { fontSize: 12, color: '#8c8c8c' }, children: ["\u05DE\u05D6\u05D4\u05D4 \u05E4\u05E0\u05D9\u05DE\u05D9: #", assessmentId] })] }), _jsx(Button, { type: "link", onClick: (e) => { e.stopPropagation(); open(); }, style: { color: '#6B21A8', fontWeight: 500, padding: 0 }, children: "\u05E4\u05EA\u05D9\u05D7\u05EA \u05D4\u05D1\u05D5\u05D7\u05DF \u2190" })] }));
}
/**
 * Empty state for the no-exam case: a short explanatory subtitle +
 * centered tall primary pill, matching our page-level action treatment.
 * `onConnect` reuses the existing chooser flow (`setQuizChooserOpen`),
 * which still routes through `PassingAssessmentChooser` to either
 * create a new exam or link an existing one.
 */
function NoExamState({ onConnect }) {
    return (_jsxs("div", { style: {
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', gap: 12, padding: '12px 8px',
        }, children: [_jsx(Typography.Text, { type: "secondary", style: { maxWidth: 440 }, children: "\u05D1\u05D5\u05D7\u05DF \u05DE\u05E2\u05D1\u05E8 \u05E0\u05D3\u05E8\u05E9 \u05DB\u05D3\u05D9 \u05E9\u05D4\u05EA\u05DC\u05DE\u05D9\u05D3 \u05D9\u05E1\u05DE\u05DF \u05D0\u05EA \u05D4\u05E9\u05D9\u05E2\u05D5\u05E8 \u05DB\u05D4\u05D5\u05E9\u05DC\u05DD. \u05D7\u05D1\u05E8\u05D5 \u05D1\u05D5\u05D7\u05DF \u05E7\u05D9\u05D9\u05DD \u05D0\u05D5 \u05E6\u05E8\u05D5 \u05D7\u05D3\u05E9." }), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: onConnect, style: { height: 44, padding: '0 20px', fontSize: 15, fontWeight: 600 }, children: "\u05D7\u05D9\u05D1\u05D5\u05E8 \u05D1\u05D5\u05D7\u05DF \u05DC\u05E9\u05D9\u05E2\u05D5\u05E8" })] }));
}
function ClipsCard({ lesson }) {
    const { data, loading, reload } = useLoader(() => listClips(lesson.id), [lesson.id]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const del = async (c) => {
        try {
            await deleteClip(c.id);
            notifySuccess('קליפ נמחק');
            reload();
        }
        catch { /* client auto-surfaces */ }
    };
    const canPreview = (c) => !!c.bunny_video_id && !!c.library_id;
    const cols = [
        {
            title: 'תצוגה',
            dataIndex: 'thumbnail_url',
            width: 140,
            render: (url) => _jsx(ClipThumb, { url: url ?? null }),
        },
        {
            title: 'כותרת',
            dataIndex: 'title',
            render: (title, c) => (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx("span", { style: { fontWeight: 600, color: '#22174B' }, children: title }), c.bunny_video_id
                        ? _jsx("span", { style: { fontSize: 11, color: '#8c8c8c' }, children: c.bunny_video_id })
                        : _jsx(Tag, { style: { marginInlineEnd: 0 }, children: "mock" })] })),
        },
        {
            title: '',
            width: 160,
            render: (_, c) => (_jsxs(Space, { size: 6, children: [_jsx(Tooltip, { title: canPreview(c) ? 'צפייה בסרטון' : 'אין וידאו להצגה', children: _jsx(Button, { className: "btn-action-round", icon: _jsx(PlayCircleOutlined, {}), disabled: !canPreview(c), onClick: () => setPreviewing(c) }) }), _jsx(Button, { className: "btn-action-round", icon: _jsx(EditOutlined, {}), onClick: () => setEditing(c) }), _jsx(Popconfirm, { title: "\u05DE\u05D7\u05D9\u05E7\u05EA \u05E7\u05DC\u05D9\u05E4", description: "\u05D4\u05E4\u05E2\u05D5\u05DC\u05D4 \u05D1\u05DC\u05EA\u05D9 \u05D4\u05E4\u05D9\u05DB\u05D4. \u05DC\u05D4\u05DE\u05E9\u05D9\u05DA?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: () => del(c), children: _jsx(Button, { className: "btn-action-round btn-action-danger", icon: _jsx(DeleteOutlined, {}) }) })] })),
        },
    ];
    return (_jsxs(Card, { title: _jsx(CardTitle, { icon: _jsx(VideoCameraOutlined, {}), text: `קליפים · ${data?.length ?? 0}` }), extra: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => setCreating(true), children: "\u05E7\u05DC\u05D9\u05E4 \u05D7\u05D3\u05E9" }), children: [(!loading && (data?.length ?? 0) === 0)
                ? _jsx(FriendlyEmpty, { icon: _jsx(VideoCameraOutlined, {}), title: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05E7\u05DC\u05D9\u05E4\u05D9\u05DD", subtitle: "\u05E6\u05D5\u05E8 \u05D0\u05EA \u05D4\u05E7\u05DC\u05D9\u05E4 \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u05E9\u05DC \u05D4\u05E9\u05D9\u05E2\u05D5\u05E8 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05E2\u05DC\u05D5\u05EA \u05D5\u05D9\u05D3\u05D0\u05D5.", cta: { label: 'קליפ חדש', onClick: () => setCreating(true) } })
                : (_jsx(SortableTable, { size: "small", loading: loading, dataSource: data ?? [], columns: cols, onReorder: async (ids) => { await reorderClips(lesson.id, ids); reload(); } })), _jsx(ClipModal, { open: creating, onClose: () => setCreating(false), onDone: () => { setCreating(false); reload(); }, lessonId: lesson.id }), editing && (_jsx(ClipModal, { open: true, onClose: () => setEditing(null), onDone: () => { setEditing(null); reload(); }, lessonId: lesson.id, initial: editing })), previewing && (_jsx(ClipPreviewModal, { clip: previewing, onClose: () => setPreviewing(null) }))] }));
}
/**
 * Small 64 pt thumbnail (rounded) for the clips table. Falls back to a
 * video-camera glyph on a muted bg when the clip has no uploaded video
 * yet (null `thumbnail_url`), or if the `<img>` fails to load — the
 * signed URL may be transiently stale.
 */
function ClipThumb({ url }) {
    const [failed, setFailed] = useState(false);
    const empty = !url || failed;
    // 128×72 — large enough to actually read the thumbnail (16:9 at 128px).
    // Rounded 10px matches the global action-button radius.
    const SIZE = { width: 128, height: 72, borderRadius: 10 };
    if (empty) {
        return (_jsx("div", { style: {
                ...SIZE,
                background: '#EFE9FA',
                color: '#8B2FD6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26,
            }, children: _jsx(VideoCameraOutlined, {}) }));
    }
    return (_jsx("img", { src: url, alt: "", width: SIZE.width, height: SIZE.height, onError: () => setFailed(true), style: { ...SIZE, objectFit: 'cover', display: 'block' } }));
}
/**
 * Friendly empty state — centered icon tile + headline + subtitle + CTA.
 * Shared pattern lives inline here; stage-2 can promote to a reusable
 * component once a second screen needs the same shape.
 */
function FriendlyEmpty({ icon, title, subtitle, cta }) {
    return (_jsxs("div", { style: {
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', padding: '32px 16px', gap: 10,
        }, children: [_jsx("div", { style: {
                    width: 72, height: 72, borderRadius: 20,
                    background: '#F1EAFF', color: '#8B2FD6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 32,
                }, children: icon }), _jsx(Typography.Title, { level: 5, style: { margin: 0, color: '#22174B' }, children: title }), _jsx(Typography.Text, { type: "secondary", style: { maxWidth: 320 }, children: subtitle }), cta && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: cta.onClick, style: { marginTop: 8 }, children: cta.label }))] }));
}
/**
 * Video preview modal — Bunny iframe embed. Our library has CDN token
 * authentication ON (used by mobile playback) but Embed-view token
 * authentication OFF, so the embed URL loads without an embed signature
 * and the embedded Bunny player signs its own CDN requests internally.
 * See docs:
 *   https://docs.bunny.net/docs/stream-embed-token-authentication
 *   https://docs.bunny.net/docs/cdn-token-authentication-basic
 */
function ClipPreviewModal({ clip, onClose }) {
    const libraryId = clip.library_id;
    const videoId = clip.bunny_video_id;
    // The parent only opens this modal when both are set, but we guard
    // defensively so a stale cache / future refactor doesn't blow up.
    const src = libraryId && videoId
        ? `https://iframe.mediadelivery.net/embed/${encodeURIComponent(libraryId)}/${encodeURIComponent(videoId)}`
        : null;
    return (_jsx(Modal, { title: `תצוגה מקדימה · ${clip.title}`, open: true, onCancel: onClose, footer: null, width: 720, destroyOnHidden: true, children: src ? (_jsx("div", { style: { position: 'relative', paddingTop: '56.25%' /* 16:9 */ }, children: _jsx("iframe", { src: src, title: clip.title, loading: "lazy", allow: "accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen", allowFullScreen: true, style: {
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    border: 0, borderRadius: 8,
                } }) })) : (_jsx(Alert, { type: "info", showIcon: true, message: "\u05D0\u05D9\u05DF \u05D5\u05D9\u05D3\u05D0\u05D5 \u05DC\u05D4\u05E6\u05D2\u05D4 \u2014 \u05D9\u05E9 \u05DC\u05D4\u05E2\u05DC\u05D5\u05EA \u05E7\u05D5\u05D3\u05DD." })) }));
}
